import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import _get from 'lodash/get';
import queryString from 'query-string';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { detect } from 'detect-browser';

import { Button, Container, Divider, Typography } from 'ravenjs';

import { REQUEST_ALREADY_SIGNED, REQUEST_INVALID_SIGNATURE_STATUS } from 'constants/errors';
import {
    ALREADY_SIGNED_HANDBOOK,
    CANCELED_REQUESTED_HANDBOOK,
    CONTACT_SUPPORT,
    CONTACT_SUPPORT_DEFAULT,
    UNABLE_REQUESTED_HANDBOOK,
    UNABLE_REQUESTED_SIGNATURE
} from 'constants/messages';
import { actions as hellosignActions, selectors as hellosignSelectors } from 'modules/hellosign';
import { buildDocumentDownloadURL } from 'utils/documents';
import { SIGN_DOC } from 'utils/constants';
import { extractErrorMessage } from 'utils/errors';

// Detect the user's current browser.
const browser = detect();

/**
 * Create a simple styled Anchor Button
 *
 * @type {Object}
 */
const AnchorButton = styled(Button)`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    min-height: inherit;
    padding: 10px 15px;
    &:hover {
        text-decoration: none;
    }
`;

class Handbook extends Component {
    static propTypes = {
        documentName     : PropTypes.string,
        fetchPdfUrl      : PropTypes.func.isRequired,
        validateToken    : PropTypes.func.isRequired,
        history          : PropTypes.object.isRequired,
        loadSigningIframe: PropTypes.func.isRequired,
        location         : PropTypes.object.isRequired
    };

    static defaultProps = {
        documentName: 'Employee Handbook'
    };

    constructor(props) {
        super(props);

        this.state = {
            disabledSignButton   : false,
            disabledViewButton   : false,
            error                : false,
            errorMessage         : '',
            contactSupportMessage: false,
            showMainPanel        : false,
            viewedHandbook       : false
        };
    }

    componentDidMount() {
        const { validateToken, history, location } = this.props;
        const search = queryString.parse(location.search);
        const token = _get(search, 'token');
        // If a token is present in the search params,
        // then fetch the pdf document blob.
        if (token) {
            validateToken(token)
                .then(response => {
                    const status = _get(response, 'info.status');

                    if (!['viewed', 'sent'].includes(status.toLowerCase())) {
                        this.handleStatusError(status);
                    } else {
                        // Update the state.
                        this.setState(() => ({
                            showMainPanel: true,
                            token
                        }));
                    }
                })
                .catch(this.handleSigningUrlError);
        } else {
            // We don't want the user to access the route unless they're
            // signing a handbook.
            history.replace('/');
        }
    }

    downloadBlobFile = (fileBlob, fileURL, token) => {
        // Build an anchor tag element.
        const anchor = document.createElement('a');
        // Build a name for the handbook.
        const handbookName = `${String(token).substr(0, 6)}-handbook.pdf`;
        // Append some attributes to the anchor tag.
        anchor.setAttribute('href', fileURL);
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('type', fileBlob.type || 'application/pdf');
        // Depending on the browser we're in,
        // we need to handle the download of file blob differently.
        switch (browser && browser.name) {
        case 'ie': {
            // Internet Explorer

            // Open the window via JS.
            window.navigator.msSaveOrOpenBlob(fileBlob, handbookName);
            break;
        }
        case 'ios':
        case 'safari': {
            const fileURL = window.URL.createObjectURL(fileBlob);
            // Safari (non-iOS)

            // NOTE: for Safari...
            // This shouldn't be blocked by popup-blockers in Safari,
            // since this is the correct behavior of saving blobs in Safari.

            // Open a new window with the PDF url via JavaScript.
            document.location = fileURL;
            break;
        }
        default: {
            // All other browsers

            // Append the anchor tag to the body.
            document.body.appendChild(anchor);
            // Simulate a click on the anchor tag.
            anchor.click();
            // After a while remove the appeneded anchor tag from the body.
            setTimeout(() => {
                document.body.removeChild(anchor);
            }, 500);
            break;
        }
        }

        // Update state
        this.setState({ disabledViewButton: false, viewedHandbook: true });
    };

    handleStatusError = status => {
        let errorMessage = UNABLE_REQUESTED_HANDBOOK;
        let defaultContactSupportMessage = CONTACT_SUPPORT;

        switch (status.toLowerCase()) {
        case 'signed':
            errorMessage = ALREADY_SIGNED_HANDBOOK;
            break;
        case 'canceled':
            errorMessage = CANCELED_REQUESTED_HANDBOOK;
            defaultContactSupportMessage = false;
            break;
        default:
            errorMessage = UNABLE_REQUESTED_SIGNATURE;
            defaultContactSupportMessage = CONTACT_SUPPORT_DEFAULT;
            break;
        }

        // Update the local state.
        this.setState(() => ({
            error                : true,
            errorMessage,
            contactSupportMessage: defaultContactSupportMessage
        }));
    };

    handleSigningUrlError = (error = {}) => {
        // Extract the error message.
        const extractedError = extractErrorMessage(error);
        const errorCode = _get(error, 'response.data.errorCode');
        const errorCodeStatus = _get(error, 'response.data.code');
        // Determine if the user has already signed the requested document.
        let errorMessage = UNABLE_REQUESTED_HANDBOOK;
        let defaultContactSupportMessage = CONTACT_SUPPORT;

        switch (parseInt(errorCode, 10)) {
        case REQUEST_ALREADY_SIGNED:
            errorMessage = ALREADY_SIGNED_HANDBOOK;
            break;
        case REQUEST_INVALID_SIGNATURE_STATUS:
            errorMessage = CANCELED_REQUESTED_HANDBOOK;
            defaultContactSupportMessage = false;
            break;
        default:
            if (errorCodeStatus === 400) {
                errorMessage = UNABLE_REQUESTED_SIGNATURE;
                defaultContactSupportMessage = CONTACT_SUPPORT_DEFAULT;
            }
            break;
        }

        // Update the local state.
        this.setState(() => ({
            error                : extractedError,
            errorMessage,
            contactSupportMessage: defaultContactSupportMessage
        }));
    };

    handleRedirect = () => {
        const { history, location } = this.props;
        // Build the redirect route.
        const redirectRoute = `/success${location.search}`;
        // Redirect the user to the '/success' route.
        history.push(redirectRoute);
    };

    handleViewHandbookClick = e => {
        const { fetchPdfUrl } = this.props;
        const { token } = this.state;
        // Stop event bubbling.
        e.stopPropagation();
        // Prevent the default action on the event.
        e.preventDefault();

        // Update state
        this.setState({ disabledViewButton: true }, () => {
            fetchPdfUrl(token)
                .then(({ fileBlob }) => {
                    // Determine the `createObjectURL` method.
                    const createObjectURL =
                        (window.URL && window.URL.createObjectURL) || function() {};
                    // Create the file URL from the resp.
                    const fileURL = createObjectURL(fileBlob);

                    this.downloadBlobFile(fileBlob, fileURL, token);
                })
                .catch(this.handleSigningUrlError);
        });
    };

    hasUserViewedHandbook = () => {
        const { viewedHandbook } = this.state;

        return viewedHandbook;
    };

    signHandbook = () => {
        const { token } = this.state;
        const { loadSigningIframe } = this.props;
        // Update state
        this.setState({ disabledSignButton: true }, () => {
            // Load the HelloSign iFrame.
            loadSigningIframe({
                redirectTo: this.handleRedirect,
                token
            }).catch(this.handleSigningUrlError);
        });
    };

    userHasViewedHandbook = () => {
        // Set the `viewedHandbook` to true.
        this.setState(() => ({ viewedHandbook: true }));
    };

    renderViewDocBtn = () => {
        const { disabledViewButton, token } = this.state;

        switch (browser && browser.os && browser.os.toLowerCase()) {
        case browser.os.toLowerCase().includes('android'): {
            // NOTE: On 'iOS' and 'Android OS'
            // we're going to convert the link into a 'download' link.

            // Build the 'href'.
            const href = buildDocumentDownloadURL(token);
            // Build the 'onClick'
            const onClick = e => {
                // Stop event bubbling.
                e.stopPropagation();
                // The user has successfully viewed the handbook.
                this.userHasViewedHandbook();
            };

            return (
                <Typography fullWidth>
                    <AnchorButton
                        as="a"
                        href={href}
                        onClick={onClick}
                        target="_blank"
                        disabled={disabledViewButton}
                    >
                        {disabledViewButton ? 'Processing...' : 'View'}
                    </AnchorButton>
                </Typography>
            );
        }
        default: {
            return (
                <Typography fullWidth>
                    <Button
                        onClick={this.handleViewHandbookClick}
                        disabled={disabledViewButton}
                    >
                        {disabledViewButton ? 'Processing...' : 'View'}
                    </Button>
                </Typography>
            );
        }
        }
    };

    render() {
        const {
            disabledSignButton,
            error,
            errorMessage,
            contactSupportMessage,
            showMainPanel
        } = this.state;
        const { documentName } = this.props;

        return (
            <Container margin="0 auto" padding="35px 15px">
                {!showMainPanel && !error && (
                    <Typography fullWidth gutterBottom="0">
                        Fetching the requested handbook...
                    </Typography>
                )}
                {showMainPanel && !error && (
                    <>
                        <Typography fullWidth>
                            Please review and acknowledge your receipt of the{' '}
                            <strong>{documentName}</strong>.
                        </Typography>
                        <br />
                        <Typography fullWidth>
                            Do not close this window. The handbook will open in a new tab for your
                            review. Return to this page to provide your e-signature once you have
                            completed reading the handbook. Upon returning, you will be able to
                            provide your e-signature.
                        </Typography>
                        <Typography fullWidth>
                            Please disable any popup blockers to allow viewing the handbook.
                        </Typography>
                        <br />
                        <Typography fullWidth>
                            {SIGN_DOC}
                        </Typography>
                        <br />
                        {this.renderViewDocBtn()}
                        {this.hasUserViewedHandbook() && (
                            <div>
                                <Divider margin="25px 0 15px" />
                                <Typography fullWidth>
                                    I acknowledge that I have received a copy of the{' '}
                                    <strong>{documentName}</strong> and that I have read it,
                                    understood it, and agree to comply with it.
                                </Typography>
                                <Typography align="right" fullWidth>
                                    <Button
                                        onClick={this.signHandbook}
                                        disabled={disabledSignButton}
                                    >
                                        {disabledSignButton ? 'Processing...' : 'Agree & Continue'}
                                    </Button>
                                </Typography>
                            </div>
                        )}
                    </>
                )}
                {error && (
                    <>
                        <Typography fullWidth gutterBottom="0">
                            {errorMessage}
                            {contactSupportMessage && (
                                <>
                                    <br />
                                    {contactSupportMessage}
                                </>
                            )}
                        </Typography>
                    </>
                )}
            </Container>
        );
    }
}

const mapStateToProps = createStructuredSelector({
    documentName: hellosignSelectors.getDocumentName
});

const mapDispatchToProps = {
    fetchPdfUrl      : hellosignActions.fetchPdfUrl,
    validateToken    : hellosignActions.validateToken,
    loadSigningIframe: hellosignActions.loadSigningIframe
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Handbook);
